import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { careService } from "../../services/careService";

const metricCards = (metrics = {}) => [
  ["ti-users-group", "Active Residents", metrics.activeResidents ?? metrics.residents ?? 0, "In this workspace", "mint"],
  ["ti-calendar-due", "NCP Due Soon", metrics.ncpDueSoon ?? metrics.carePlansDueSoon ?? 0, "Due in next 7 days", "purple"],
  ["ti-alert-triangle", "Open Incidents", metrics.openIncidents ?? metrics.alerts ?? 0, "Need attention", "amber"],
  ["ti-user-check", "Staff On Duty", metrics.staffOnDuty ?? metrics.staff ?? 0, "Across all shifts", "blue"],
];

const residentRow = (resident = {}) => [
  resident.initials || resident.name?.slice(0, 2)?.toUpperCase() || "--",
  resident.name || resident.residentName || "Unnamed resident",
  resident.room ? `Room ${resident.room}` : resident.location || "",
  resident.currentIssue || resident.issue || resident.ncpStatus || "Review",
  resident.priority || resident.alertPriority || "Low",
  resident.dueDate || resident.nextReview || "",
  resident.status || "",
  resident.id || resident.residentId,
];

const carePlanRow = (plan = {}) => [
  plan.initials || plan.residentName?.slice(0, 2)?.toUpperCase() || "--",
  plan.residentName || plan.resident || "Unnamed resident",
  plan.room ? `Room ${plan.room}` : plan.location || "",
  plan.status || "Draft",
  plan.dueDate || plan.nextReview || "",
  plan.dueStatus || plan.statusNote || "",
  plan.residentId,
];

function DataTable({ rows, type }) {
  return (
    <div className="data-table" role="table">
      <div className="table-row table-head" role="row">
        <span>Resident</span>
        <span>{type === "plans" ? "Status" : "Issue"}</span>
        <span>{type === "plans" ? "Due Date" : "Priority"}</span>
        <span>{type === "plans" ? "" : "Due Date"}</span>
      </div>
      {rows.map((row) => (
        <Link className="table-row" to={row[7] || row[6] ? `/residents/${row[7] || row[6]}` : "/residents"} role="row" key={`${type}-${row[1]}-${row[4]}`}>
          <span className="resident-cell"><b>{row[0]}</b><span><strong>{row[1]}</strong><small>{row[2]}</small></span></span>
          <span>{type === "plans" ? <em className={`pill ${row[3].includes("Review") ? "purple" : "blue"}`}>{row[3]}</em> : row[3]}</span>
          <span>{type === "plans" ? row[4] : <em className={`pill ${row[4].toLowerCase()}`}>{row[4]}</em>}</span>
          <span className={row[row.length - 1] === "Overdue" ? "danger-text" : ""}>{type === "plans" ? row[5] : row[5]}</span>
        </Link>
      ))}
    </div>
  );
}

export default function DashboardPage() {
  const [dashboard, setDashboard] = useState({ metrics: {}, residents: [], carePlans: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    careService.dashboard()
      .then((data) => {
        if (!active) return;
        setDashboard({
          metrics: data?.metrics || data?.summary || {},
          residents: Array.isArray(data?.residents) ? data.residents : [],
          carePlans: Array.isArray(data?.carePlans) ? data.carePlans : [],
        });
        setError("");
      })
      .catch((err) => {
        if (!active) return;
        setDashboard({ metrics: {}, residents: [], carePlans: [] });
        setError(err?.response?.data?.message || err?.message || "Unable to load care dashboard.");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const metrics = metricCards(dashboard.metrics);
  const residents = dashboard.residents.map(residentRow);
  const carePlans = dashboard.carePlans.map(carePlanRow);

  return (
    <CareShell>
      <div className="page-title">
        <h1>Care Dashboard</h1>
        <p>Overview of care operations and tasks that need your attention.</p>
      </div>
      {error ? <p className="form-error">{error}</p> : null}
      <div className="metric-grid">
        {metrics.map(([icon, label, value, note, tone]) => (
          <div className="metric-card" key={label}>
            <span className={`round-icon ${tone}`}><i className={`ti ${icon}`} /></span>
            <div><strong>{label}</strong><b>{value}</b><small>{note}</small></div>
          </div>
        ))}
      </div>
      <div className="dashboard-grid full-width-grid">
        <Panel title="Residents Requiring Attention" action="View all">
          {loading ? <p>Loading residents...</p> : residents.length ? <DataTable rows={residents} type="residents" /> : <p>No residents requiring attention.</p>}
          <Link className="text-action align-end" to="/residents">View all residents <i className="ti ti-arrow-right" /></Link>
        </Panel>
        <Panel title="Negotiated Care Plans Due Soon" action="View all">
          {loading ? <p>Loading care plans...</p> : carePlans.length ? <DataTable rows={carePlans} type="plans" /> : <p>No care plans due soon.</p>}
          <Link className="text-action align-end" to="/care-plans/negotiated">View all care plans <i className="ti ti-arrow-right" /></Link>
        </Panel>
        <Panel title="Incidents Overview">
          <div className="chart-line" />
          <Link className="text-action align-end" to="/dashboard">View all incidents <i className="ti ti-arrow-right" /></Link>
        </Panel>
        <Panel title="Assessments Overview">
          <div className="donut-wrap">
            <div className="donut"><strong>62%</strong><span>Completed</span></div>
            <div className="legend"><span><b className="teal" />Completed 80</span><span><b className="blue" />In Progress 34</span><span><b className="red" />Overdue 14</span></div>
          </div>
          <Link className="text-action align-end" to="/assessments">View all assessments <i className="ti ti-arrow-right" /></Link>
        </Panel>
      </div>
    </CareShell>
  );
}
